import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { isVersionConflict } from "../lib/api";
import { formatDateTime } from "../lib/missions";
import { FILE_KINDS, isVisible, valueLabel } from "../lib/procedures";
import {
  DECISION_LABELS, REPORT_STATUS, answerBody, contributionKey, contributionOf, factLines, reportErrorMessage, waitingFor,
} from "../lib/missionReports";
import ChecklistItemField from "../components/procedures/ChecklistItemField";
import { ReceiptPreviewDialog } from "../components/finance/ReceiptPreview";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Camera, Check, ChevronDown, CircleAlert, FileText, Loader2, Paperclip, Printer, RefreshCw, X } from "lucide-react";
import { useBottomBar } from "../hooks/useCopilotDock";

const DRAFT_KINDS = ["text", "number"]; // saved when the field is left, not on every key
const TEXT_MAX = 5000;
const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// The report of a mission (Ordre 15), phone first: every item saved on its own (nothing lost if the network drops between two),
// each person's contribution, « Soumettre » at the bottom; for an organiser who did not write it, « Valider » or « Renvoyer ».
export default function MissionReport() {
  const { id: missionId } = useParams();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [missing, setMissing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // return | reopen | submit
  const [preview, setPreview] = useState(null);
  const dataRef = useRef(null);
  const queues = useRef({});
  const base = `/missions/${missionId}/report`;
  const caps = data?.capabilities || {};
  useBottomBar(Boolean(caps.submit || caps.review));

  const apply = (next) => { dataRef.current = next; setData(next); };
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPhase("loading");
    try {
      const { data: next } = await api.get(base);
      apply(next);
      setPhase("ready");
    } catch (e) {
      if (e?.response?.status === 404) { setPhase("missing"); return; }
      setErrorText(reportErrorMessage(e, "Impossible de charger le compte rendu."));
      setPhase("error");
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  // One save at a time per field, each built from the latest version the server returned.
  const queue = (key, request) => {
    const previous = queues.current[key] || Promise.resolve();
    const next = previous.then(async () => {
      setSaving((s) => ({ ...s, [key]: "saving" }));
      try {
        const { data: saved } = await request();
        apply(saved);
        setSaving((s) => ({ ...s, [key]: "saved" }));
        setMissing((m) => (m ? saved.report.missing : m));
        setDrafts((d) => { const { [key]: _drop, ...rest } = d; return rest; });
      } catch (e) {
        setSaving((s) => ({ ...s, [key]: "error" }));
        toast.error(reportErrorMessage(e));
        if (isVersionConflict(e) || e?.response?.status === 409) {
          setDrafts((d) => { const { [key]: _drop, ...rest } = d; return rest; });
          load({ silent: true });
        }
      }
    });
    queues.current[key] = next;
    return next;
  };
  const saveAnswer = (item, changes) => queue(`item:${item.id}`,
    () => api.put(`${base}/answers/${item.id}`, answerBody(dataRef.current.report.answers[item.id], changes)));
  const saveContribution = (member, text) => queue(`contribution:${contributionKey(member)}`, () => api.put(`${base}/contribution`, {
    expected_version: contributionOf(dataRef.current.report, member).version, member: { kind: member.kind, id: member.id }, text,
  }));

  const act = async (request, success) => {
    setBusy(true);
    try {
      await Promise.all(Object.values(queues.current));
      const { data: next } = await request();
      apply(next);
      setDialog(null);
      setMissing(null);
      toast.success(success);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "report_incomplete") {
        setMissing(detail.missing);
        setDialog(null);
        document.getElementById(`report-item-${detail.missing[0]?.item_id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        toast.error(reportErrorMessage(e));
        if (isVersionConflict(e)) load({ silent: true });
      }
      return false;
    } finally {
      setBusy(false);
    }
  };
  const start = () => act(() => api.post(base), "Compte rendu commencé");
  const submit = () => act(() => api.post(`${base}/submit`, { expected_version: dataRef.current.report.version }),
    dataRef.current.report.validation_required ? "Compte rendu soumis à validation" : "Compte rendu publié");
  const approve = () => act(() => api.post(`${base}/approve`, { expected_version: dataRef.current.report.version }), "Compte rendu validé");

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="report-unavailable">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? "Cette mission n'existe pas ou vous n'y avez pas accès." : errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to={`/missions/${missionId}`}>Retour à la mission</Link></Button>
          {phase === "error" && <Button variant="outline" onClick={() => load()}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>}
        </div>
      </Card>
    );
  }

  const { mission, report } = data;
  const status = report ? REPORT_STATUS[report.status] : null;
  const waiting = waitingFor(data);
  const tz = mission.timezone;

  return (
    <div className="space-y-4 pb-28" data-testid="report-page">
      <Link to={`/missions/${missionId}`} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 print:hidden">
        <ArrowLeft size={14} /> {mission.reference} · {mission.title}
      </Link>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
            {status && <span className={`rounded-full px-2 py-0.5 font-medium ${status.tone}`} data-testid="report-status">{status.label}</span>}
            {report && <span>{report.template.label} · v{report.template.number}</span>}
            {report?.revision > 0 && <span>Version soumise {report.revision}</span>}
          </div>
          <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="report-title">Compte rendu de mission</h1>
          <p className="text-sm text-slate-500">{mission.title} · {mission.validation_required ? "à faire valider par un responsable" : "publié dès qu'il est soumis"}</p>
        </div>
        {report && (
          <Button variant="outline" size="icon" aria-label="Imprimer" className="shrink-0 print:hidden" onClick={() => window.print()} data-testid="report-print">
            <Printer size={16} />
          </Button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] items-start print:block">
        {/* The main column opens with a section title (h2 24px + 12px gap): drop the side cards by the same height so both columns' first cards line up. */}
        <aside className={`space-y-4 lg:order-2 print:mb-4 ${report?.sections.length ? "lg:pt-9" : ""}`}>
          {waiting && <p className="text-sm text-slate-600" data-testid="report-waiting">{waiting}</p>}
          {report?.review && (
            <Card className={`p-4 text-sm space-y-1 ${report.review.decision === "approved" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="report-review">
              <p className="font-medium text-slate-900">{DECISION_LABELS[report.review.decision]} par {report.review.by_name} le {formatDateTime(report.review.at, tz)}</p>
              {report.review.reason && <p className="text-slate-700">Motif : {report.review.reason}</p>}
            </Card>
          )}
          {missing?.length > 0 && (
            <Card className="p-4 border-red-200 bg-red-50 space-y-1.5" role="alert" data-testid="report-missing">
              <p className="text-sm font-medium text-red-900">Pour soumettre, complétez :</p>
              <ul className="list-disc pl-5 text-sm text-red-900">{missing.map((m) => <li key={m.item_id}>{m.label}</li>)}</ul>
            </Card>
          )}

          <FactsCard facts={data.facts} />
          {report && <Revisions report={report} tz={tz} />}
        </aside>

        <div className="space-y-4 min-w-0 lg:order-1">
          {!report ? (
            <Card className="p-8 text-center space-y-3" data-testid="report-empty">
              <FileText className="mx-auto text-slate-300" size={26} />
              <p className="text-sm text-slate-600">Le compte rendu dit ce qui a été fait, ce qui a coincé et ce qu'il faut faire ensuite.</p>
              {caps.start && <Button onClick={start} disabled={busy} data-testid="report-start">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Rédiger le compte rendu</Button>}
            </Card>
          ) : (
            <>
              {report.sections.map((section) => (
                <section key={section.id} className="space-y-3" data-testid={`report-section-${section.id}`}>
                  <h2 className="text-base font-semibold text-slate-900">{section.title}</h2>
                  {section.items.filter((item) => isVisible(item, report.answers)).map((item) => (
                    <ReportItem key={item.id} item={item} answer={report.answers[item.id]} editable={caps.write} tz={tz} draft={drafts[`item:${item.id}`]}
                      status={saving[`item:${item.id}`]} missing={missing?.some((m) => m.item_id === item.id)}
                      onDraft={(v) => setDrafts((d) => ({ ...d, [`item:${item.id}`]: v }))} onSave={(changes) => saveAnswer(item, changes)}
                      onPreview={(f) => setPreview({ source: { key: `file:${f.id}`, path: `/files/${f.id}` }, name: f.name })} />
                  ))}
                </section>
              ))}
              <Contributions report={report} contributors={data.contributors} editable={caps.write} drafts={drafts} saving={saving} tz={tz}
                onDraft={(key, v) => setDrafts((d) => ({ ...d, [key]: v }))} onSave={saveContribution} />
              {caps.reopen && (
                <div className="print:hidden"><Button variant="outline" onClick={() => setDialog("reopen")} data-testid="report-reopen">Rouvrir le compte rendu</Button></div>
              )}
            </>
          )}
        </div>
      </div>

      {(caps.submit || caps.review) && (
        <div className="fixed inset-x-0 bottom-[4.75rem] lg:bottom-4 z-30 px-4 lg:left-64 print:hidden" data-testid="report-bar">
          <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-3 shadow-lg flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600 min-w-0">{report.progress.answered} / {report.progress.total} élément{report.progress.total > 1 ? "s" : ""}</span>
            <div className="flex gap-2 shrink-0">
              {caps.submit && (
                <Button onClick={() => setDialog("submit")} disabled={busy} data-testid="report-submit">
                  {report.validation_required ? "Soumettre" : "Publier"}
                </Button>
              )}
              {caps.review && (
                <>
                  <Button variant="outline" onClick={() => setDialog("return")} disabled={busy} data-testid="report-return">Renvoyer</Button>
                  <Button onClick={approve} disabled={busy} data-testid="report-approve">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Valider</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={dialog === "submit"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent data-testid="report-submit-dialog">
          <DialogHeader>
            <DialogTitle>{report?.validation_required ? "Soumettre le compte rendu ?" : "Publier le compte rendu ?"}</DialogTitle>
            <DialogDescription>
              {report?.validation_required
                ? "Il ne se modifiera plus jusqu'à la décision d'un responsable, qui pourra le valider ou vous le renvoyer avec un motif."
                : "Il ne se modifiera plus, sauf réouverture par un responsable. Cette version est gardée."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={busy}>Annuler</Button>
            <Button onClick={submit} disabled={busy} data-testid="report-submit-confirm">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{report?.validation_required ? "Soumettre" : "Publier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReasonDialog open={dialog === "return" || dialog === "reopen"} mode={dialog} busy={busy} onClose={() => setDialog(null)}
        onConfirm={(reason) => act(() => api.post(`${base}/${dialog === "return" ? "return" : "reopen"}`, { expected_version: dataRef.current.report.version, reason }),
          dialog === "return" ? "Compte rendu renvoyé pour correction" : "Compte rendu rouvert")} />
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Pièce jointe au compte rendu." />
    </div>
  );
}

function FactsCard({ facts }) {
  const lines = factLines(facts);
  if (!lines.length) return null;
  return (
    <Card className="p-4 space-y-1" data-testid="report-facts">
      <p className="text-sm font-medium text-slate-900">La mission en bref</p>
      <ul className="text-sm text-slate-600 space-y-0.5">{lines.map((line) => <li key={line}>{line}</li>)}</ul>
    </Card>
  );
}

function SaveState({ status, testid }) {
  if (status === "saving") return <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Enregistrement…</span>;
  if (status === "saved") return <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid={testid}><Check size={12} />Enregistré</span>;
  if (status === "error") return <span className="text-xs text-red-700 inline-flex items-center gap-1"><CircleAlert size={12} />Non enregistré</span>;
  return null;
}

function ReportItem({ item, answer, editable, tz, draft, status, missing, onDraft, onSave, onPreview }) {
  const value = draft !== undefined ? draft : answer?.value ?? null;
  const change = (v) => (DRAFT_KINDS.includes(item.kind) ? onDraft(v) : onSave({ value: v }));
  const blur = () => {
    if (draft === undefined) return;
    if (String(draft ?? "") !== String(answer?.value ?? "")) onSave({ value: draft });
    else onDraft(undefined);
  };
  const files = FILE_KINDS.includes(item.kind);
  const filled = answer && (answer.value !== null || answer.files?.length);
  return (
    <Card id={`report-item-${item.id}`} className={`p-4 space-y-2 ${missing ? "border-red-300" : ""}`} data-testid={`report-item-${item.id}`}>
      {editable ? (
        <ChecklistItemField item={item} value={value} onChange={change} onBlur={blur} testid={`report-field-${item.id}`} maxLength={TEXT_MAX} rows={item.kind === "text" ? 5 : 3}
          fileSlot={files ? <FileSlot item={item} answer={answer} onSave={onSave} onPreview={onPreview} /> : null} />
      ) : (
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-900">{item.label}</p>
          {files ? <FileList files={answer?.files || []} onPreview={onPreview} />
            : <p className="text-sm text-slate-700 whitespace-pre-line" data-testid={`report-value-${item.id}`}>{filled ? valueLabel(item, answer.value) : <span className="text-slate-400">Non renseigné</span>}</p>}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
        <span>{filled ? `Saisi par ${answer.recorded_by_name} · ${formatDateTime(answer.recorded_at, tz)}` : ""}</span>
        <SaveState status={status} testid={`report-item-saved-${item.id}`} />
      </div>
    </Card>
  );
}

function FileList({ files, onPreview, onRemove }) {
  if (!files.length) return <p className="text-sm text-slate-400">Aucun fichier</p>;
  return (
    <ul className="flex flex-wrap gap-2">
      {files.map((f) => (
        <li key={f.id} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 pl-2 text-sm">
          <button type="button" onClick={() => onPreview(f)} className="max-w-[12rem] truncate py-1 hover:underline">{f.name}</button>
          {onRemove && <button type="button" aria-label={`Retirer ${f.name}`} onClick={() => onRemove(f)} className="p-1.5 text-slate-500 hover:text-red-700"><X size={13} /></button>}
        </li>
      ))}
    </ul>
  );
}

function FileSlot({ item, answer, onSave, onPreview }) {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const files = answer?.files || [];
  const photo = item.kind === "photo";
  const upload = async (picked) => {
    if (!picked.length) return;
    setUploading(true);
    const ids = [];
    try {
      for (const file of picked) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files", form, { params: { context: "mission_report" }, headers: { "Idempotency-Key": newKey() } });
        ids.push(data.id);
      }
    } catch (e) {
      toast.error(reportErrorMessage(e, "Fichier refusé : PDF ou image (JPEG, PNG, WebP) de 10 Mo maximum."));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
    if (ids.length) await onSave({ file_ids: [...files.map((f) => f.id), ...ids] });
  };
  return (
    <div className="space-y-2">
      {files.length > 0 && <FileList files={files} onPreview={onPreview} onRemove={(f) => onSave({ file_ids: files.filter((x) => x.id !== f.id).map((x) => x.id) })} />}
      <input ref={input} type="file" className="hidden" multiple accept={photo ? "image/jpeg,image/png,image/webp" : "image/jpeg,image/png,image/webp,application/pdf"}
        {...(photo ? { capture: "environment" } : {})} onChange={(e) => upload(Array.from(e.target.files || []))} data-testid={`report-file-input-${item.id}`} />
      <Button type="button" variant="outline" className="h-11" disabled={uploading} onClick={() => input.current?.click()}>
        {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : photo ? <Camera size={16} className="mr-2" /> : <Paperclip size={16} className="mr-2" />}
        {photo ? "Prendre ou joindre une photo" : "Joindre un fichier"}
      </Button>
    </div>
  );
}

// One contribution per person of the team: the caller writes their own, those of colleagues without account, everyone's if organiser.
function Contributions({ report, contributors, editable, drafts, saving, tz, onDraft, onSave }) {
  const writable = editable ? contributors : [];
  const writableKeys = new Set(writable.map(contributionKey));
  const others = (report.contributions || []).filter((c) => !writableKeys.has(contributionKey(c.member)) && c.text);
  if (!writable.length && !others.length) return null;
  return (
    <section className="space-y-3" data-testid="report-contributions">
      <h2 className="text-base font-semibold text-slate-900">Contributions de l'équipe</h2>
      {writable.map((member) => {
        const key = `contribution:${contributionKey(member)}`;
        const current = contributionOf(report, member);
        const value = drafts[key] !== undefined ? drafts[key] : current.text || "";
        return (
          <Card key={key} className="p-4 space-y-2" data-testid={`report-contribution-${member.kind}-${member.id}`}>
            <Label htmlFor={key} className="text-sm">{member.name}{member.kind === "collaborator" ? " (sans compte)" : ""}</Label>
            <Textarea id={key} rows={3} maxLength={5000} className="text-base md:text-sm" value={value} placeholder="Ce que cette personne a fait, constaté ou recommande."
              onChange={(e) => onDraft(key, e.target.value)} data-testid={`report-contribution-input-${member.kind}-${member.id}`}
              onBlur={() => { if (drafts[key] !== undefined && (drafts[key].trim() || null) !== (current.text || null)) onSave(member, drafts[key]); }} />
            <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
              <span>{current.version ? `Saisi par ${current.recorded_by_name} · ${formatDateTime(current.recorded_at, tz)}` : ""}</span>
              <SaveState status={saving[key]} testid={`report-contribution-saved-${member.kind}-${member.id}`} />
            </div>
          </Card>
        );
      })}
      {others.map((c) => (
        <Card key={contributionKey(c.member)} className="p-4 space-y-1" data-testid={`report-contribution-${c.member.kind}-${c.member.id}`}>
          <p className="text-sm font-medium text-slate-900">{c.member.name}</p>
          <p className="text-sm text-slate-700 whitespace-pre-line">{c.text}</p>
          <p className="text-xs text-slate-500">Saisi par {c.recorded_by_name} · {formatDateTime(c.recorded_at, tz)}</p>
        </Card>
      ))}
    </section>
  );
}

function Revisions({ report, tz }) {
  const [open, setOpen] = useState(false);
  if (!report.revisions?.length) return null;
  return (
    <Card className="p-4 print:hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="text-sm font-medium text-slate-900 inline-flex items-center gap-1"
        data-testid="report-revisions-toggle">
        <ChevronDown size={14} className={open ? "rotate-180" : ""} />Versions soumises ({report.revisions.length})
      </button>
      {open && (
        <ol className="space-y-2 pt-3" data-testid="report-revisions">
          {[...report.revisions].reverse().map((r) => (
            <li key={r.number} className="text-sm">
              <div className="text-slate-800">Version {r.number} · soumise par {r.submitted_by_name} le {formatDateTime(r.submitted_at, tz)}</div>
              {r.decision && (
                <div className="text-xs text-slate-600">{DECISION_LABELS[r.decision.decision]} par {r.decision.by_name} le {formatDateTime(r.decision.at, tz)}
                  {r.decision.reason ? ` · motif : ${r.decision.reason}` : ""}</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function ReasonDialog({ open, mode, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  const returning = mode === "return";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="report-reason-dialog">
        <DialogHeader>
          <DialogTitle>{returning ? "Renvoyer pour correction ?" : "Rouvrir le compte rendu ?"}</DialogTitle>
          <DialogDescription>
            {returning ? "L'équipe pourra le corriger puis le soumettre de nouveau. Le motif lui est montré et reste dans l'historique."
              : "Il repasse « à corriger » : l'équipe pourra le modifier et le soumettre de nouveau. Les versions déjà soumises restent."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="report-reason">Motif *</Label>
          <Textarea id="report-reason" rows={3} maxLength={500} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="report-reason" placeholder={returning ? "Ex. Précisez les postes installés." : "Ex. Erreur dans la date d'installation."} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={() => onConfirm(reason.trim())} disabled={busy || reason.trim().length < 3} data-testid="report-reason-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{returning ? "Renvoyer" : "Rouvrir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
